import SvgLayer from "./SvgLayer";
import React, { useEffect, useState, useRef } from "react";
import { DraggableCore, DraggableData } from "react-draggable";
import {
  MouseTouchEvent,
  MouseEvent,
} from "react-draggable/build/cjs/utils/types";

import { getOffset, getOffsetProc, offsetToKey, getPosFromKey } from "../chessUtils/util";
import { ANIMATION_DURATION, Z_UNDER } from "./animations";
import DrawLayer from "./DrawLayer";
import { Key } from "./types";
import { MoveShort, Square } from "@chesshotel/chess";

const isSafari =
  process.browser &&
  /Safari/.test(navigator.userAgent) &&
  /Apple Computer/.test(navigator.vendor);

export const isRightButton = (e: MouseTouchEvent): boolean =>
  e.buttons === 2 || e.button === 2;

export default function DragBoard({
  freeMove,
  lastPuzzleMove,
  puzzleDone,
  pieceRefs,
  movePiece,
  possibleMoves,
  squareSize,
  playingWhite,
  position,
  highLightKey,
  bestMove,
  chessTermArrows,
  showLastMove,
  lastMove,
  lastMoveColor,
  settings,
  moveType,
  moveTypeKey,
  children,
  highlightSquares,
}: {
  freeMove: boolean;
  pieceRefs: any;
  possibleMoves: any;
  squareSize: number;
  playingWhite: boolean;
  highLightKey: Key | null;
  movePiece: any;
  position: any;
  bestMove: MoveShort | null;
  chessTermArrows: any;
  showLastMove: boolean;
  lastMove: any;
  lastMoveColor: any;
  settings: any;
  lastPuzzleMove: any;
  puzzleDone: boolean;
  moveType: any;
  moveTypeKey: Key | null;
  highlightSquares: Square[];
  children: React.ReactNode;
}) {
  const disableInteractions = !squareSize;
  const halfSquare = squareSize / 2;
  const [goast, setGoast] = useState(null);
  const [moveHelp, setMoveHelp] = useState(null);
  const [moveSquarePos, setMoveSquarePos] = useState(null);
  const [shapes, setShapes] = useState([]);

  const [selectedKey, setSelectedKey] = useState<Key | null>(null);
  const [hoveredKey, setHoveredKey] = useState<Key | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);

  // Use refs instead of module-level variables to prevent stuck pieces
  const lastDragKeyRef = useRef<Key | null>(null);
  const currentKeyRef = useRef<Key | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const hasDraggedRef = useRef(false);
  const wasSelectedRef = useRef<Key | null>(null);

  function isOwnPiece(key: Key) {
    const piece = position?.[key];
    return piece && (freeMove ||
      (playingWhite && piece.color === "white") ||
      (!playingWhite && piece.color === "black"));
  }

  function clearSelection() {
    setSelectedKey(null);
    setMoveHelp(null);
    setHoveredKey(null);
  }

  function canMoveFrom(from: Key, to: Key) {
    const moves = possibleMoves[from];
    return Array.isArray(moves) && moves.indexOf(to) >= 0;
  }

  function canMove(to: Key) {
    return canMoveFrom(currentKeyRef.current, to);
  }

  function getHoveredKeyFromEvent(
    e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ): Key | null {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect || !squareSize) return null;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) {
      return null;
    }

    return offsetToKey(x, y, playingWhite, squareSize) as Key;
  }

  function onBoardMouseMove(
    e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ) {
    if (!selectedKey) {
      if (hoveredKey) {
        setHoveredKey(null);
      }
      return;
    }

    const key = getHoveredKeyFromEvent(e);
    if (key !== hoveredKey) {
      setHoveredKey(key);
    }
  }

  useEffect(() => {
    if (dragIdRef.current && possibleMoves[currentKeyRef.current]) {
      if (canMove(lastDragKeyRef.current as Key)) {
        const dragPos = getPosFromKey(lastDragKeyRef.current as Key, playingWhite);
        setMoveSquarePos(dragPos);
      }
    }
  }, [possibleMoves]);

  function onDrag(e, data) {
    if (!dragIdRef.current) {
      return;
    }

    hasDraggedRef.current = true;

    const posProc = {
      x: ((data.x - halfSquare) * 100) / squareSize,
      y: ((data.y - halfSquare) * 100) / squareSize,
    };

    const pieceRef = pieceRefs.current[dragIdRef.current];
    if (pieceRef) {
      pieceRef.style.transform = `translate(${posProc.x}%, ${posProc.y}%)`;
    }
    const dragKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;

    if (lastDragKeyRef.current != dragKey) {
      if (canMove(dragKey)) {
        setMoveSquarePos(getPosFromKey(dragKey, playingWhite));
      } else {
        setMoveSquarePos(null);
      }
      lastDragKeyRef.current = dragKey;
    }
  }

  function onStop(e: MouseTouchEvent, data: DraggableData) {
    if (!dragIdRef.current) {
      return;
    }
    setMoveSquarePos(null);

    const to = offsetToKey(data.x, data.y, playingWhite, squareSize) as Key;
    const pieceRef = pieceRefs.current[dragIdRef.current];
    if (canMove(to)) {
      const goal = getOffsetProc(to, playingWhite);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      }
      movePiece({ from: currentKeyRef.current, to });
    } else if (to === currentKeyRef.current && isOwnPiece(currentKeyRef.current)) {
      // Ended on same square — treat as click to select/deselect
      const goal = getOffset(currentKeyRef.current, playingWhite, squareSize);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
      if (pieceRef && pieceRefs.current[dragIdRef.current]) {
        pieceRefs.current[dragIdRef.current].style.zIndex = Z_UNDER;
      }
      setGoast(null);
      if (wasSelectedRef.current === currentKeyRef.current) {
        // Was already selected — deselect
        wasSelectedRef.current = null;
        setMoveHelp(null);
        setSelectedKey(null);
        setHoveredKey(null);
      } else {
        setSelectedKey(currentKeyRef.current);
        // Keep moveHelp visible for click-to-move
      }
      lastDragKeyRef.current = null;
      dragIdRef.current = null;
      currentKeyRef.current = null;
      return;
    } else {
      const goal = getOffset(currentKeyRef.current, playingWhite, squareSize);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
    }

    if (pieceRef && pieceRefs.current[dragIdRef.current]) {
      pieceRefs.current[dragIdRef.current].style.zIndex = Z_UNDER;
    }

    setGoast(null);
    settings.pieceDestinations && setMoveHelp(null);
    // Clear refs to prevent stuck state
    lastDragKeyRef.current = null;
    dragIdRef.current = null;
    currentKeyRef.current = null;
  }

  function onStartDrag(e: MouseTouchEvent, data: DraggableData) {
    if (isSafari) {
      e.preventDefault();
    }
    if (!position) {
      return;
    }
    const startKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;

    // Click-to-move: if a piece is selected, handle the second click
    if (selectedKey) {
      if (canMoveFrom(selectedKey, startKey)) {
        // Animate the piece to the destination
        const selectedPiece = position[selectedKey];
        if (selectedPiece) {
          const pieceEl = pieceRefs.current[selectedPiece.id];
          if (pieceEl) {
            const start = getOffset(selectedKey, playingWhite, squareSize);
            const goal = getOffset(startKey, playingWhite, squareSize);
            pieceEl.style.zIndex = 300;
            // Set final position as inline style so it takes effect when animation ends
            pieceEl.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
            pieceEl.animate(
              [
                { transform: `translate(${start.x}px, ${start.y}px)` },
                { transform: `translate(${goal.x}px, ${goal.y}px)` },
              ],
              {
                duration: ANIMATION_DURATION,
                easing: "ease-in-out",
              }
            );
          }
        }
        movePiece({ from: selectedKey, to: startKey });
        clearSelection();
        return;
      }
      wasSelectedRef.current = selectedKey === startKey ? startKey : null;
      clearSelection();
      // Fall through to drag setup — allows dragging the selected piece directly
    }

    hasDraggedRef.current = false;
    currentKeyRef.current = startKey;
    const piece = position[startKey];
    if (isOwnPiece(startKey)) {
      const { color, role } = piece;
      dragIdRef.current = piece.id;
      const pieceRef = pieceRefs.current[piece.id];
      if (pieceRef) {
        pieceRef.style.zIndex = 300;
      }
      setGoast({
        goastPos: getPosFromKey(startKey, playingWhite),
        goastPiece: { color, role },
      });

      settings.pieceDestinations && setMoveHelp(possibleMoves[startKey]);
    }
  }

  function startDraw(e: MouseEvent) {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e as unknown as globalThis.MouseEvent).clientX - rect.left;
    const y = (e as unknown as globalThis.MouseEvent).clientY - rect.top;
    const currentPos = [Math.floor(x / squareSize), Math.floor(y / squareSize)];
    setShapes([...shapes, { from: currentPos }]);
  }

  function onDraw(e: MouseTouchEvent, data: DraggableData) {
    const currentPos = [
      Math.floor(data.x / squareSize),
      Math.floor(data.y / squareSize),
    ];
    const newShapes = [...shapes];
    newShapes[newShapes.length - 1].to = currentPos;
    setShapes(newShapes);
  }
  function onStopDraw(e: MouseTouchEvent, data: DraggableData) {}

  const boardContent = (
    <div
      ref={boardRef}
      className="relative flex w-full h-full"
      style={{
        cursor:
          selectedKey && hoveredKey && canMoveFrom(selectedKey, hoveredKey)
            ? "pointer"
            : "default",
      }}
      onMouseMove={onBoardMouseMove}
      onMouseLeave={() => setHoveredKey(null)}
    >
      <DrawLayer
        lastPuzzleMove={lastPuzzleMove}
        goast={goast}
        moveSquarePos={moveSquarePos}
        moveHelp={moveHelp}
        position={position}
        highLightKey={highLightKey}
        playingWhite={playingWhite}
        showLastMove={showLastMove}
        lastMove={lastMove}
        lastMoveColor={lastMoveColor}
        settings={settings}
        puzzleDone={puzzleDone}
        moveType={moveType}
        moveTypeKey={moveTypeKey}
        highlightSquares={highlightSquares}
        squareSize={squareSize}
        selectedKey={selectedKey}
      />
      <SvgLayer
        shapes={shapes}
        bestMove={bestMove}
        chessTermArrows={chessTermArrows}
        playingWhite={playingWhite}
      />
      {children}
    </div>
  );

  if (disableInteractions) {
    return boardContent;
  }

  return (
    <>
      <DraggableCore
        enableUserSelectHack={false}
        onStart={(e: MouseTouchEvent, data: DraggableData) => {
          if (!e.shiftKey) {
            onStartDrag(e, data);
          }
        }}
        onDrag={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onDraw(e, data) : onDrag(e, data);
        }}
        onStop={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onStopDraw(e, data) : onStop(e, data);
        }}
        onMouseDown={(e: MouseEvent) => {
          if (e.shiftKey) {
            setDrawMode(true);
            startDraw(e);
          } else {
            setDrawMode(false);
            setShapes([]);
          }
        }}
      >
        {boardContent}
      </DraggableCore>
    </>
  );
}
